import { useCallback, useEffect, useState } from "react";
import { fetchLists, addListOption, deleteListOption } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HTTP_CONFLICT } from "@/lib/http";
import { Plus, Trash2, Tag, Users, CreditCard } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "purpose", label: "Zweck", icon: Tag, singular: "Zweck" },
  { key: "person", label: "Person / Firma", icon: Users, singular: "Person / Firma" },
  {
    key: "payment_method",
    label: "Zahlungsmittel",
    icon: CreditCard,
    singular: "Zahlungsmittel",
  },
];

function ListPanel({ type, singular, items, onAdd, onDelete }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const v = value.trim();
    if (!v) return;
    setBusy(true);
    await onAdd(type, v);
    setValue("");
    setBusy(false);
  };

  return (
    <div className="space-y-5">
      <form onSubmit={submit} className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={`${singular} hinzufügen …`}
          className="bg-white"
          data-testid={`add-input-${type}`}
        />
        <Button
          type="submit"
          disabled={busy}
          className="gap-2 shrink-0"
          data-testid={`add-list-item-button-${type}`}
        >
          <Plus className="h-4 w-4" /> Hinzufügen
        </Button>
      </form>

      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground border border-dashed border-border p-8 text-center">
          Noch keine Einträge.
        </div>
      ) : (
        <div className="border border-border bg-white divide-y divide-border">
          {items.map((it) => (
            <div
              key={it.id}
              className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors duration-150"
              data-testid={`list-item-${type}`}
            >
              <span className="font-medium">{it.value}</span>
              {!it.protected && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDelete(it.id)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  data-testid={`delete-list-item-button-${it.id}`}
                  title="Löschen"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ManageLists() {
  const [lists, setLists] = useState({
    purpose: [],
    person: [],
    payment_method: [],
  });
  const [active, setActive] = useState("purpose");

  const load = useCallback(async () => {
    try {
      setLists(await fetchLists());
    } catch {
      toast.error("Stammdaten konnten nicht geladen werden.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async (type, value) => {
    try {
      await addListOption(type, value);
      toast.success(`„${value}" hinzugefügt.`);
      load();
    } catch (e) {
      if (e?.response?.status === HTTP_CONFLICT) toast.message("Eintrag existiert bereits.");
      else toast.error("Hinzufügen fehlgeschlagen.");
    }
  };

  const handleDelete = async (optId) => {
    try {
      await deleteListOption(optId);
      toast.success("Eintrag gelöscht.");
      load();
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const activeTab = TABS.find((t) => t.key === active) || TABS[0];

  return (
    <div className="max-w-3xl" data-testid="manage-lists">
      <div className="mb-8">
        <h1 className="font-heading text-4xl tracking-tight font-medium">
          Einstellungen
        </h1>
        <p className="text-muted-foreground mt-1">
          Verwalten Sie die Auswahllisten für die Beleg-Anreicherung.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="manage-lists-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.key;
          return (
            <Button
              key={t.key}
              variant={isActive ? "default" : "secondary"}
              onClick={() => setActive(t.key)}
              className={
                isActive
                  ? "gap-2 bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                  : "gap-2 border border-border"
              }
              data-testid={`tab-${t.key}`}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </Button>
          );
        })}
      </div>

      <ListPanel
        key={activeTab.key}
        type={activeTab.key}
        singular={activeTab.singular}
        items={lists[activeTab.key] || []}
        onAdd={handleAdd}
        onDelete={handleDelete}
      />
    </div>
  );
}
